import { FormErrorSummary } from '@/components/forms/form-error-summary';
import FormProvider from '@/components/forms/hook-form';
import { ChoicesOrderUnitMeasure } from '@/sections/management/accounts/orders/context';
import {
  useLegalEntities,
  ILegalEntitySelectOption,
} from '@/sections/management/accounts/orders/hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import { Card, Grid, Button, CardHeader, CardContent } from '@mui/material';
import { useMemo, useState, useEffect, useCallback, SyntheticEvent } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CreateDiscountRequest,
  CreateBillingOrderRequest,
} from '@/services/billing/types/create-billing.type';
import { useBillingStore } from '../../store/billing.store';
import {
  formatFormValues,
  formatSalesOrder,
  groupUniqueItems,
  GroupedUniqueItem,
  getTotalMustBeBilled,
  buildDistribuitionListData,
  groupUniqueUnitMeasureDistribution,
} from '../../utils/billing.utils';
import { LegalEntityDistributionList } from '../distribution';
import { DistributionUnitSelector } from '../input/distribution-unit-selector';
import { LegalEntityMultipleSelect } from '../input/legal-entity-select';
import { BillingOrderSummary } from './billing-order-summary';
import { createBillingOrderSecondStepSchema } from './schema/createBillingOrderSecondStepSchema';
import { OrderTypes } from '@/types/order';
import { IOrderDocument } from '@/types/order-document';
import { useBoolean, useDecimalFormatter } from '@hooks/index';
import OrderUploadOVDialog from '@/sections/management/accounts/orders/forms/documents/order-upload-ov';

interface BillingOrderNewEditSecondStepFormProps {
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
}

export interface TotalMustBeBilled {
  itemId: string; //this should have the format subscription-orderType
  total: number;
}
export interface SecondStepFormValues {
  legalEntity: ILegalEntitySelectOption;
  items: SecondStepFormItem[];
  documents?: IOrderDocument[];
}

export interface SecondStepFormItem {
  itemId: string;
  value: number;
  amount: number;
  quantity: number;
  unitMeasure: ChoicesOrderUnitMeasure;
  itemInfo: GroupedUniqueItem;
}

export interface BillingOrderSecondStepFormValues {
  legalEntities: ILegalEntitySelectOption[];
  unitMeasureDistribution: Record<string, ChoicesOrderUnitMeasure>;
  enableDiscounts: boolean;
  formValues: SecondStepFormValues[];
  totalMustBeBilled: TotalMustBeBilled[];
  discounts: CreateDiscountRequest[];
}

export const BillingOrderNewEditSecondStepForm = ({
  onNext,
  onBack,
  onCancel,
}: BillingOrderNewEditSecondStepFormProps) => {
  const { t, i18n } = useTranslation();
  const formatter = useDecimalFormatter(i18n.language);

  const openUploadFile = useBoolean();
  const [documentLegalEntityId, setDocumentLegalEntityId] = useState<string | null>(null);
  const [documentFormValueIndex, setDocumentFormValueIndex] = useState<number>(0);

  const organization = useBillingStore((state) => state.organization);
  const selectedMilestones = useBillingStore((state) => state.selectedMilestones);
  const legalEntityDistributions = useBillingStore((state) => state.legalEntityDistributions);
  const addLegalEntityDistribution = useBillingStore((state) => state.addLegalEntityDistribution);
  const setBillingOrder = useBillingStore((state) => state.setBillingOrder);
  const discounts = useBillingStore((state) => state.discounts);
  const storedSecondStepFormValues = useBillingStore((state) => state.secondStepFormValues);
  const setSecondStepFormValues = useBillingStore((state) => state.setSecondStepFormValues);

  const groupedUniqueItems = useMemo(() => {
    return groupUniqueItems(selectedMilestones);
  }, [selectedMilestones]);

  const distribuitionListItem = useMemo(() => {
    return buildDistribuitionListData(legalEntityDistributions);
  }, [legalEntityDistributions]);

  const { data: legalEntitiesData, isLoading: isLoadingLegalEntities } = useLegalEntities(
    organization?._id
  );

  const discountAmount = useMemo(() => {
    return discounts.reduce((sum, discount) => sum + discount.amount, 0);
  }, [discounts]);

  const totalMustBeBilled = useMemo(() => {
    return getTotalMustBeBilled(groupedUniqueItems);
  }, [groupedUniqueItems]);

  const secondStepSchema = useMemo(
    () =>
      createBillingOrderSecondStepSchema({
        t,
        i18n,
        formatter,
        itemsMustBeBilled: totalMustBeBilled,
        discountAmount,
      }),
    [t, i18n, formatter, totalMustBeBilled, discountAmount]
  );

  const defaultValues: BillingOrderSecondStepFormValues = useMemo(() => {
    const unitMeasureDistribution = groupUniqueUnitMeasureDistribution(groupedUniqueItems);

    const formValues =
      storedSecondStepFormValues.length > 0
        ? storedSecondStepFormValues
        : formatFormValues(legalEntityDistributions, groupedUniqueItems);

    return {
      legalEntities: [],
      unitMeasureDistribution: unitMeasureDistribution,
      enableDiscounts: discounts.length > 0,
      formValues,
      totalMustBeBilled: totalMustBeBilled,
      discounts: discounts,
    };
  }, [
    organization,
    selectedMilestones,
    groupedUniqueItems,
    storedSecondStepFormValues,
    totalMustBeBilled,
    discounts,
  ]);

  const methods = useForm<BillingOrderSecondStepFormValues>({
    resolver: yupResolver(secondStepSchema),
    defaultValues,
  });

  const {
    setValue,
    handleSubmit,
    clearErrors,
    watch,
    getValues,
    formState: { errors },
  } = methods;

  const watchedValues = watch();

  const onSubmit = handleSubmit(async (data) => {
    const salesOrders = formatSalesOrder(data.formValues, data.totalMustBeBilled, data.discounts);

    if (!organization) {
      return;
    }

    const createBillingOrderRequest: CreateBillingOrderRequest = {
      salesOrder: salesOrders,
      discounts: data.discounts,
      order: {
        organization: organization._id,
        acceptedTermsAndConditions: true,
        onlySave: false,
        orderType: OrderTypes.BILLING_ORDER,
        milestones: selectedMilestones.map((milestone) => milestone._id),
      },
    };
    setBillingOrder(createBillingOrderRequest);
    setSecondStepFormValues(data.formValues);
    clearErrors();
    onNext();
  });

  const handleChangeLegalEntity = useCallback(
    (event: SyntheticEvent<Element, Event>, newValue: ILegalEntitySelectOption[] | null) => {
      if (newValue) {
        const newLegalEntityDistributions = addLegalEntityDistribution(newValue);
        const {unitMeasureDistribution} = watchedValues;
        if (newLegalEntityDistributions.length === 1) {
          Object.keys(watchedValues.unitMeasureDistribution).forEach((key) => {
            unitMeasureDistribution[key] = ChoicesOrderUnitMeasure.amount;
          });

          setValue('unitMeasureDistribution', unitMeasureDistribution);
        }

        const formValues = formatFormValues(newLegalEntityDistributions, groupedUniqueItems);

        setValue('formValues', formValues);

        setValue('legalEntities', newValue);
      }
    },
    [addLegalEntityDistribution, setValue, watchedValues.unitMeasureDistribution, discountAmount]
  );

  const handleGoBack = useCallback(() => {
    setSecondStepFormValues(watchedValues.formValues);
    onBack();
  }, [onBack, watchedValues.formValues]);

  const handleAddDocument = useCallback(
    (legalEntityId: string) => {
      const formValueIndex = watchedValues.formValues?.findIndex(
        (fv: SecondStepFormValues) => fv.legalEntity._id === legalEntityId
      );
      setDocumentLegalEntityId(legalEntityId);
      setDocumentFormValueIndex(formValueIndex >= 0 ? formValueIndex : 0);
      setTimeout(() => {
        openUploadFile.onTrue();
      }, 200);
    },
    [watchedValues.formValues, openUploadFile]
  );

  //useEffect use to mount persistent data
  useEffect(() => {
    const legalEntities: ILegalEntitySelectOption[] = [];
    legalEntityDistributions.forEach((led) => {
      const findedLegalEntity = legalEntitiesData?.find((le) => le._id === led.legalEntity._id);
      if (findedLegalEntity) {
        legalEntities.push(findedLegalEntity);
      }
    });
    setValue('legalEntities', legalEntities);
  }, [legalEntitiesData]);
  return (
    <>
      <FormProvider methods={methods} onSubmit={onSubmit}>
        {/* Legal Entity Selection */}
        <Card sx={{ mb: 2 }}>
          <CardHeader title={t('Billing Distribution')} />
          <CardContent>
            <Grid container spacing={1} sx={{ pt: 0 }}>
              <Grid item xs={12}>
                <LegalEntityMultipleSelect
                  isLoading={isLoadingLegalEntities}
                  onChange={handleChangeLegalEntity}
                  options={legalEntitiesData || []}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Distribution Unit for multiple legal entities */}
        <DistributionUnitSelector visible={legalEntityDistributions.length > 1} />

        {/* Legal Entity Distribution */}
        <LegalEntityDistributionList
          formValues={watchedValues.formValues}
          distributions={distribuitionListItem}
          visible={distribuitionListItem.length > 0}
          onAddDocument={handleAddDocument}
        />

        {/* Distribution Summary */}
        {watchedValues.formValues && watchedValues.formValues.length > 0 && <BillingOrderSummary />}

        <FormErrorSummary errors={errors} />

        <Grid container>
          <Grid item xs={4} sx={{ textAlign: 'left' }}>
            <LoadingButton
              color="primary"
              onClick={handleGoBack}
              variant="contained"
              sx={{ mr: 1 }}
              loading={isLoadingLegalEntities}
            >
              {t('Back')}
            </LoadingButton>

            <Button variant="outlined" sx={{ mr: 1 }} onClick={onCancel}>
              {t('Cancel')}
            </Button>
          </Grid>

          <Grid
            item
            xs={8}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {/* TODO: To be implemented in another ticket */}
            {/* 
            <LoadingButton
              color="secondary"
              type="button"
              onClick={onSaveDraft}
              sx={{ mr: 1 }}
              variant="contained"
              loading={isLoadingLegalEntities}
            >
              {t('Save Draft')}
            </LoadingButton> */}
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              onClick={onSubmit}
              loading={isLoadingLegalEntities}
            >
              {t('Next')}
            </LoadingButton>
          </Grid>
        </Grid>
      </FormProvider>

      {openUploadFile.value && documentLegalEntityId !== null && organization && (
        <OrderUploadOVDialog
          open={openUploadFile.value}
          onClose={() => {
            openUploadFile.onFalse();
          }}
          organizationId={organization._id}
          setExternalValue={setValue}
          index={documentFormValueIndex}
          getValues={getValues}
          documentFieldPath={`formValues.${documentFormValueIndex}.documents`}
          currentDocument={
            watchedValues.formValues?.[documentFormValueIndex]?.documents?.[0] || null
          }
          sx={{ zIndex: 1300 }}
        />
      )}
    </>
  );
};
